import React, { createContext, useContext, useState, useEffect } from "react";
import apiConfig from "../config/apiConfig";
import { apiFetch } from "../apis/apiFetch";
import Cookies from "js-cookie";
import { LoginContext } from "./LoginContext";
import { resolveAdmissionDriveKind } from "../utils/applicationFlow";

interface StudentProgress {
    personalDetails: boolean;
    parentDetails: boolean;
    educationDetails: boolean;
    examDetails: boolean; // ✅ NEW
    programmeDetails: boolean;
    paymentDone: boolean;
    declarationDone: boolean;
    loading: boolean;
}

const StudentProgressContext = createContext<{
    progress: StudentProgress;
    refreshProgress: () => Promise<void>;
    isAppEdit: boolean
    isAppSubmitted: boolean
} | null>(null);

export const StudentProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [progress, setProgress] = useState<StudentProgress>({
        personalDetails: false,
        parentDetails: false,
        educationDetails: false,
        examDetails: false, // ✅ NEW
        programmeDetails: false,
        paymentDone: false,
        declarationDone: false,
        loading: true,
    });
    const [isAppEdit, setIsAppEdit] = useState(false)
    const [isAppSubmitted, setIsAppSubmitted] = useState(false)

    const { user } = useContext(LoginContext);
    const userId = user?.userId;

    const refreshProgress = async () => {
        if (!userId) {
            setProgress(prev => ({ ...prev, loading: false }));
            return;
        }

        try {
            const applicantRes = await apiFetch(
                `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${userId}`
            );

            const applicantData = await applicantRes.json();
            const row = applicantData.resource?.[0];

            let editingAllowed = true;
            if (row && row.drive_id) {
                const driveRes = await apiFetch(
                    `${apiConfig.getResourceUrl('drive')}?queryId=GET_BY_ID&args=id:${row.drive_id}`
                );
                if (driveRes.ok) {
                    const driveData = await driveRes.json();
                    const drive = driveData.resource?.[0];
                    if (drive && drive.alloweditingenddate) {
                        const parseSafeDate = (dateStr: string) => {
                            if (!dateStr) return new Date(NaN);
                            let cleaned = dateStr.replace(/[\u202F\u00A0]/g, ' ');
                            cleaned = cleaned.replace(/,\s*$/, "").trim();
                            return new Date(cleaned);
                        };
                        const endDate = parseSafeDate(drive.alloweditingenddate);
                        if (!isNaN(endDate.getTime())) {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            editingAllowed = today <= endDate;
                        }
                    }
                }
            }

            setIsAppEdit(row?.status === "EDITING" && editingAllowed)
            setIsAppSubmitted(row?.status === "SUBMITTED" || row?.status === "EDIT_SUBMIT" || (row?.status === "EDITING" && !editingAllowed))
            const personalDetails =
                applicantData.resource && applicantData.resource.length > 0 && applicantData.resource[0].p_pincode !== null && applicantData.resource[0].p_pincode !== undefined;

            const { kind } = await resolveAdmissionDriveKind(userId);

            // Check Parent Details (Guardian)
            const parentDetails = applicantData.resource && applicantData.resource[0].guardian_name !== null && applicantData.resource[0].guardian_name !== undefined;

            // Check Education Details (Graduation details)
            const educationDetails = kind === "btech" ?
                (
                    row.edu12_passingyear != "" &&
                    row.edu12_passingyear != null
                )
                : applicantData.resource && applicantData.resource[0].collegename !== null && applicantData.resource[0].collegename !== undefined;


            // ✅ NEW: Exam Details (ONLY for BTech)
            const examDetails =
                row &&
                    kind === "btech"
                    ? (row.jeemain_rollno != "" &&
                        row.jeemain_rollno != null)
                    : true; // non-btech skips exam step

            // Check Programme Preference
            const programmeDetails =
                applicantData.resource &&
                applicantData.resource[0].pref1 !== null &&
                applicantData.resource[0].pref1 !== "" &&
                applicantData.resource[0].pref1 !== undefined && (
                    kind === "btech"
                        ? examDetails // 👈 depends on exam now
                        : true
                );

            const paymentDone = true;

            const declarationDone =
                Cookies.get("declarationAgreed") === "true";

            setProgress({
                personalDetails,
                parentDetails,
                educationDetails,
                examDetails, // ✅ NEW
                programmeDetails,
                paymentDone,
                declarationDone,
                loading: false,
            });
        } catch (error) {
            console.error("Failed to fetch progress", error);
            setProgress(prev => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        if (userId) {
            refreshProgress();
        }
    }, [userId]);

    return (
        <StudentProgressContext.Provider value={{ progress, refreshProgress, isAppEdit, isAppSubmitted }}>
            {children}
        </StudentProgressContext.Provider>
    );
};

export const useStudentProgress = () => {
    const context = useContext(StudentProgressContext);
    if (!context) {
        throw new Error(
            "useStudentProgress must be used within a StudentProgressProvider"
        );
    }
    return context;
};